'use client';

/**
 * TestCaseDetailsLoader - BFF data loader for schema-driven test case details
 *
 * Calls data gateway (test_details strategy) which assembles case info + stats.
 * Passes the response to DetailsPanel for schema-driven rendering.
 *
 * Same pattern as EntityDetailsLoader but routed through the testing BFF.
 *
 * @layer presentation
 * @arch_component_type wrapper
 * @arch_domain testing
 */

import { useState, useEffect } from 'react';
import { api } from '@/lib/api/client';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { DetailsPanel } from './details-panel';

interface TestCaseDetailsLoaderProps {
  entityId: string;
}

export function TestCaseDetailsLoader({ entityId }: TestCaseDetailsLoaderProps) {
  const testDetailStrategy = useCapabilityStore((s) => s.strategyName('testing', 'detail'));
  const [data, setData] = useState<Record<string, unknown>>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!entityId) return;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const result = await api.getStrategyData(testDetailStrategy ?? 'test_details', {
          entity_id: entityId,
        });
        setData((result as any)?.data?.data ?? {});
      } catch {
        setData({});
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [entityId]);

  return (
    <DetailsPanel
      entityType="test_case"
      entityId={entityId}
      data={data}
      isLoading={isLoading}
    />
  );
}
